import React, { useState, useEffect, KeyboardEvent, useMemo, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { RefreshProjectProps } from "../../types/screensTypes";
import * as Yup from "yup";
import {
    createCartelQueueAI,
    getAdminInfo,
    getCartelAIWaiting,
    addItemToProjectAI,
    createProjectCartel,
} from "../../api";
import { getRagFolders } from "../../api/ragFolders";
import { getBrandVoices, BrandVoice } from "../../api/brandVoice";
import { getAudienceProfiles, AudienceProfile } from "../../api/audienceProfile";
import AddCartelToProjectModal from "../../components/modals/AddCartelToProjectModal";
import Icon from "../../components/common/Icon";
import styles from "./TextCreation.module.css";
import CreateBrandVoiceModal from "../../components/modals/CreateBrandVoiceModal";
import EditAudienceProfileModal from "../resources/audienceProfiles/EditAudienceProfileModal";
import { useGlobalState } from "../../context/useGlobalState";
import { User } from "../../types/types";
import { RagFolder } from "../../types/requestsOutputs";
import Button from "../../components/ui/Button";
import TextInput from "../../components/ui/TextInput";
import AddToProjectButton from "../../components/ui/AddToProjectButton";
import { Tooltip } from "@mui/material";
import AIGenerationStatusModal from "../../components/modals/AIGenerationStatusModal";
import Tag from "../../components/common/Tag";
import ToggleSwitch from "../../components/common/ToggleSwitch";
import { FastField, Field, Formik, useFormikContext } from "formik";
import { DropdownField, MultiSelectDropdownField, NumberField, TextField } from "../../components/common/FormFields";
import FormGroup from "../../components/common/FormGroup";
import { COMPANY_ADDONS, hasAddon } from "../../utils/addons";
import { AI_MODEL_TYPES } from "../../constants";
import { useRemainingTokens } from "../../hooks/useRemainingTokens";
import { isQuotaError } from "../../utils/quotaError";
import stepStyles from "../track/create/createStep.module.css";

interface CartelFormValues {
    connectDatabase: boolean;
    selectedContentLanguage: string;
    textObject: string;
    textTitle: string;
    numberOfWords: number;
    audience: string;
    numberStories: number;
    requiredContent: string[];
    selectedFolders: string[];
    restrictToDatabase: boolean;
    brandVoiceId: string;
    audienceProfileId: string;
}

const CartelCreationForm: React.FC<{
    handlecartelAIAwaiting: (id: string) => Promise<void>;
    id?: string;
    user?: User | null;
    nonEmptyRagFolders: RagFolder[];
    brandVoices: BrandVoice[];
    audienceProfiles: AudienceProfile[];
    isCreateBrandVoiceModalOpen: boolean;
    onOpenCreateBrandVoiceModal: () => void;
    onCloseCreateBrandVoiceModal: () => void;
    onBrandVoiceCreated: (brandVoiceId: string) => void;
    isCreateAudienceProfileModalOpen: boolean;
    onOpenCreateAudienceProfileModal: () => void;
    onCloseCreateAudienceProfileModal: () => void;
    onAudienceProfileCreated: (audienceProfileId: string) => void;
}> = ({
    handlecartelAIAwaiting,
    id,
    user,
    nonEmptyRagFolders,
    brandVoices,
    audienceProfiles,
    isCreateBrandVoiceModalOpen,
    onOpenCreateBrandVoiceModal,
    onCloseCreateBrandVoiceModal,
    onBrandVoiceCreated,
    isCreateAudienceProfileModalOpen,
    onOpenCreateAudienceProfileModal,
    onCloseCreateAudienceProfileModal,
    onAudienceProfileCreated,
}) => {
    const { values, setFieldValue, resetForm } = useFormikContext<CartelFormValues>();

    const { connectDatabase, numberStories, requiredContent, brandVoiceId } = values;

    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const { data } = useGlobalState();

    const [contentInput, setContentInput] = useState<string>("");

    const sortedLanguages = useMemo(() => {
        if (!data?.languagesAI) return [];
        return [...data.languagesAI].sort((a, b) => a.label.localeCompare(b.label));
    }, [data?.languagesAI]);

    /**
     * Handles adding a new required content item when pressing Enter
     */
    const handleContentKeyDown = (e: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        if (e.key === "Enter" && contentInput.trim() !== "") {
            if (requiredContent.length < numberStories) {
                if (contentInput.trim().length <= 80) {
                    setFieldValue("requiredContent", [...requiredContent, contentInput.trim()]);
                    setContentInput("");
                }
            }
            e.preventDefault();
        }
    };

    /**
     * Removes a content item from the list
     */
    const handleRemoveContent = (index: number) => {
        const updatedContent = [...requiredContent];
        updatedContent.splice(index, 1);
        setFieldValue("requiredContent", updatedContent);
    };

    const canAddContent =
        contentInput.trim() !== "" && requiredContent.length < numberStories && contentInput.length <= 80;

    const handleAddContent = () => {
        if (!canAddContent) return;
        setFieldValue("requiredContent", [...requiredContent, contentInput.trim()]);
        setContentInput("");
    };

    const hasRequiredContentError = useMemo(
        () => requiredContent.length > numberStories,
        [requiredContent, numberStories],
    );

    useEffect(() => {
        if (id && id.trim() !== "") {
            handlecartelAIAwaiting(id);
        } else {
            resetForm();
        }
    }, [id, handlecartelAIAwaiting, resetForm]);

    useEffect(() => {
        if (connectDatabase && nonEmptyRagFolders.length > 0) {
            const allFolderIds = nonEmptyRagFolders.map((folder) => folder.id);
            setFieldValue("selectedFolders", allFolderIds);
        } else if (!connectDatabase) {
            setFieldValue("selectedFolders", []);
        }
    }, [connectDatabase, nonEmptyRagFolders, setFieldValue]);

    return (
        <div className={stepStyles.stepDescription}>
            {/* Language */}
            <FormGroup
                name="selectedContentLanguage"
                title={t("language")}
                style={{ marginTop: 0, maxWidth: "220px" }}
                size="small"
                required
            >
                <Field
                    name="selectedContentLanguage"
                    label={t("textCreationLanguageHelpText")}
                    component={DropdownField}
                    options={sortedLanguages}
                    loading={false}
                    error={false}
                    placeholder={t("selectALanguage")}
                    isWithoutMargin={true}
                />
            </FormGroup>

            {/* Title */}
            <FormGroup
                name="textTitle"
                title={t("title")}
                style={{ marginTop: 20, maxWidth: "220px" }}
                size="small"
                required
                tooltip={t("textTitleTooltip")}
            >
                <FastField
                    name="textTitle"
                    component={TextField}
                    placeholder={t("titleOfTheText")}
                    size={"small"}
                    style={{ marginTop: "8px" }}
                />
            </FormGroup>

            {/* Sources */}
            {user?.cmpany && hasAddon(user.cmpany, COMPANY_ADDONS.DATABASE) && (
                <>
                    <FormGroup
                        name="connectDatabase"
                        title={t("sources")}
                        style={{ marginTop: 20, maxWidth: "220px" }}
                        size="small"
                        disabledTooltip={nonEmptyRagFolders.length === 0 ? t("noNonEmptyRagFoldersAvailable") : ""}
                        required
                    >
                        <Field
                            name="connectDatabase"
                            component={DropdownField}
                            label={t("doYouWantToConnectDatabase")}
                            options={[
                                {
                                    value: true,
                                    label: t ? t("yes") : "Yes",
                                },
                                {
                                    value: false,
                                    label: t ? t("no") : "No",
                                },
                            ]}
                            error={false}
                            placeholder={t("selectAnOption")}
                            isWithoutMargin={true}
                            disabled={nonEmptyRagFolders.length === 0}
                        />
                    </FormGroup>
                </>
            )}

            {/* Restrict to Database Toggle - only for owners */}
            {connectDatabase && (
                <FormGroup
                    name="restrictToDatabase"
                    title={t("restrictToDatabase")}
                    tooltip={t("restrictToDatabaseTooltip")}
                    style={{ marginTop: 20, maxWidth: "220px" }}
                    size="small"
                >
                    <div style={{ display: "flex", alignItems: "center", marginTop: "8px" }}>
                        <ToggleSwitch
                            checked={values.restrictToDatabase}
                            onChange={(checked) => setFieldValue("restrictToDatabase", checked)}
                        />
                        <span style={{ marginLeft: "10px", fontSize: "14px" }}>
                            {values.restrictToDatabase ? t("yes") : t("no")}
                        </span>
                    </div>
                </FormGroup>
            )}

            {/* Folder Selection */}
            {connectDatabase && nonEmptyRagFolders.length > 0 && (
                <FormGroup
                    name="selectedFolders"
                    title={t("selectFolders")}
                    subtitle={t("selectFoldersToFilter")}
                    style={{ marginTop: 20, maxWidth: "220px" }}
                    size="small"
                    required
                >
                    <Field
                        name="selectedFolders"
                        component={MultiSelectDropdownField}
                        placeholder={t("selectFoldersPlaceholder")}
                        options={nonEmptyRagFolders.map((folder) => ({
                            value: folder.id,
                            label: t("folderWithFileCount", {
                                name: folder.name,
                                count: folder.fileCount,
                            }),
                        }))}
                        onChange={(selectedValues: string[]) => {
                            setFieldValue("selectedFolders", selectedValues);
                        }}
                        value={values.selectedFolders}
                        isWithoutMargin
                    />
                </FormGroup>
            )}

            {/* Number of sections */}
            <FormGroup
                name="numberStories"
                title={t("numberOfSections")}
                subtitle={t("numberOfSectionsHelptext")}
                style={{ marginTop: 20, maxWidth: "220px" }}
                size="small"
                required
            >
                <FastField component={NumberField} name="numberStories" min={1} max={20} />
            </FormGroup>

            {/* Overall Topic */}
            <FormGroup
                name="textObject"
                title={t("thematics")}
                subtitle={t("whatThematicsShouldYourCartelAddress")}
                style={{ marginTop: 20, maxWidth: "220px" }}
                size="small"
                required
            >
                <Field name="textObject" component={TextField} placeholder={t("topicExample")} />
            </FormGroup>

            {/* Required specific content */}
            <FormGroup
                title={t("requiredSpecificContent")}
                subtitle={t("specifyRequiredContentLabelCartel")}
                tooltip={t("requiredContentInfoCartel")}
                size="small"
            >
                <div className={styles.enterHint}>{t("pressEnterToAddContent")}</div>
                <Tooltip
                    title={requiredContent.length >= numberStories ? t("maxContentItemsReached") : ""}
                    arrow
                    placement="top"
                >
                    <div
                        className={styles.tagInput}
                        style={{
                            cursor: requiredContent.length >= numberStories ? "not-allowed" : undefined,
                        }}
                    >
                        <TextInput
                            placeholder={t("specifyRequiredContentExample")}
                            value={contentInput}
                            size="small"
                            onChange={(e) => setContentInput(e.target.value)}
                            onKeyDown={handleContentKeyDown}
                            disabled={requiredContent.length >= numberStories}
                        />
                        <Button
                            label={t("add")}
                            variant="soft"
                            isSmall
                            className={styles.tagAddBtn}
                            disabled={!canAddContent}
                            onClick={handleAddContent}
                        />
                    </div>
                </Tooltip>
                {contentInput.length > 80 && (
                    <div className={styles.contentWarning}>
                        {t("contentItemTooLong", { count: contentInput.length })}
                    </div>
                )}
                <div className={`${styles.tagMeta}${hasRequiredContentError ? ` ${styles.tagMetaError}` : ""}`}>
                    <span>
                        {contentInput.length}/80 {t("charactersLimit")}
                    </span>
                    <span>
                        {requiredContent.length}/{numberStories} {t("specified")}
                    </span>
                </div>
                {requiredContent.length > 0 && (
                    <div className={styles.tagList}>
                        {requiredContent.map((item, index) => (
                            <Tag
                                key={`content-${index}`}
                                text={item}
                                onRemove={() => handleRemoveContent(index)}
                                backgroundColor={hasRequiredContentError ? "var(--danger)" : "var(--green-100)"}
                                color={hasRequiredContentError ? "#fff" : "var(--green-800)"}
                            />
                        ))}
                    </div>
                )}
                {hasRequiredContentError && (
                    <div className={styles.contentWarning}>{t("tooManyRequiredContentItems")}</div>
                )}
            </FormGroup>

            {/* Number of Words */}
            <FormGroup
                name="numberOfWords"
                title={t("numberOfWords")}
                subtitle={t("numberOfWordsHelptext")}
                style={{ marginTop: 20, maxWidth: "220px" }}
                size="small"
                required
            >
                <FastField
                    component={NumberField}
                    name="numberOfWords"
                    min={50}
                    max={5000}
                    placeholder={t("enterNumberOfWords")}
                />
            </FormGroup>

            {/* Audience Profile */}
            <FormGroup
                name="audienceProfileId"
                title={t("audienceProfile")}
                subtitle={t("audienceProfileHelpText")}
                style={{ marginTop: 20, maxWidth: "220px" }}
                size="small"
            >
                <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                    <Field
                        name="audienceProfileId"
                        component={DropdownField}
                        options={[
                            { value: "__none__", label: t("noAudience") },
                            ...audienceProfiles.map((ap) => ({
                                value: ap.id,
                                label: ap.name,
                            })),
                            ...(audienceProfiles.length < 10
                                ? [{ value: "create_new", label: t("createNewAudienceProfile") || "Create New..." }]
                                : []),
                        ]}
                        error={false}
                        placeholder={t("noAudience")}
                        isWithoutMargin={true}
                        onChange={(value: string) => {
                            if (value === "create_new") {
                                onOpenCreateAudienceProfileModal();
                                setFieldValue("audienceProfileId", "");
                            } else {
                                setFieldValue("audienceProfileId", !value || value === "__none__" ? "" : value);
                            }
                        }}
                    />
                    {values.audienceProfileId && values.audienceProfileId !== "" && (
                        <div style={{ fontSize: "12px", color: "#666", fontStyle: "italic" }}>
                            {t("audienceProfileSelected")}:{" "}
                            {audienceProfiles.find((ap) => ap.id === values.audienceProfileId)?.name}
                        </div>
                    )}
                </div>
            </FormGroup>

            {/* Writing Style (formerly Brand Voice) */}
            <FormGroup
                name="brandVoiceId"
                title={t("brandVoice")}
                subtitle={t("brandVoiceHelpText")}
                style={{ marginTop: 20, maxWidth: "220px" }}
                size="small"
            >
                <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                    <Field
                        name="brandVoiceId"
                        component={DropdownField}
                        label={t("selectBrandVoice")}
                        options={[
                            { value: "__none__", label: t("noVoice") },
                            ...brandVoices.map((bv) => ({
                                value: bv.id,
                                label: bv.name,
                            })),
                            ...(brandVoices.length < 10
                                ? [{ value: "create_new", label: t("createNewBrandVoice") || "Create New..." }]
                                : []),
                        ]}
                        error={false}
                        placeholder={t("noVoice")}
                        isWithoutMargin={true}
                        onChange={(value: string) => {
                            if (value === "create_new") {
                                onOpenCreateBrandVoiceModal();
                                setFieldValue("brandVoiceId", "");
                            } else {
                                setFieldValue("brandVoiceId", !value || value === "__none__" ? "" : value);
                            }
                        }}
                    />
                    {brandVoiceId && brandVoiceId !== "" && (
                        <div style={{ fontSize: "12px", color: "#666", fontStyle: "italic" }}>
                            {t("brandVoiceSelected")}: {brandVoices.find((bv) => bv.id === brandVoiceId)?.name}
                        </div>
                    )}
                </div>
            </FormGroup>
        </div>
    );
};

const CartelCreation: React.FC<RefreshProjectProps> = ({ refreshProjects }) => {
    const { t: rawT, i18n } = useTranslation();

    const t = rawT as any;
    const { id } = useParams<{ id?: string }>();
    const navigate = useNavigate();

    const [user, setUser] = useState<any>(null);
    const [textGenerated, setTextGenerated] = useState("");
    const [loadingTextGeneration, setLoadingTextGeneration] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [showStatusModal, setShowStatusModal] = useState(false);
    const [waitingRank, setWaitingRank] = useState<number | null>(null);
    const [generationStatus, setGenerationStatus] = useState<"waiting" | "success" | "error">("waiting");
    const [errorReason, setErrorReason] = useState<"generic" | "quota">("generic");
    const [nonEmptyRagFolders, setNonEmptyRagFolders] = useState<RagFolder[]>([]);
    const [brandVoices, setBrandVoices] = useState<BrandVoice[]>([]);
    const [audienceProfiles, setAudienceProfiles] = useState<AudienceProfile[]>([]);
    const [isCreateBrandVoiceModalOpen, setIsCreateBrandVoiceModalOpen] = useState(false);
    const [isCreateAudienceProfileModalOpen, setIsCreateAudienceProfileModalOpen] = useState(false);
    const setFieldValueRef = useRef<((field: string, value: any) => void) | null>(null);
    const { isTextExhausted, refetch: refetchTokens, data: tokenData } = useRemainingTokens();

    useEffect(() => {
        const fetchUser = async () => {
            const token = localStorage.getItem("token");
            if (!token) return;
            try {
                const response = await getAdminInfo({ token });
                setUser(response);
            } catch (err) {
                console.error(err);
            }
        };

        fetchUser();
    }, []);

    useEffect(() => {
        const fetchRagFolders = async () => {
            const token = localStorage.getItem("token") || "";
            try {
                const folders = await getRagFolders({ token });
                // Filter non-empty folders
                const nonEmpty = folders.filter((folder) => !folder.isEmpty);
                setNonEmptyRagFolders(nonEmpty);
            } catch (err) {
                console.error(err);
            }
        };

        if (user?.cmpany && hasAddon(user.cmpany, COMPANY_ADDONS.DATABASE)) {
            fetchRagFolders();
        }
    }, [user]);

    useEffect(() => {
        const fetchBrandVoices = async () => {
            try {
                const voices = await getBrandVoices();
                setBrandVoices(voices);
            } catch (err) {
                console.error("Error fetching brand voices:", err);
            }
        };

        fetchBrandVoices();
    }, []);

    useEffect(() => {
        const fetchAudienceProfiles = async () => {
            try {
                const profiles = await getAudienceProfiles();
                setAudienceProfiles(profiles);
            } catch (err) {
                console.error("Error fetching audience profiles:", err);
            }
        };

        fetchAudienceProfiles();
    }, []);

    const validationSchema = Yup.object({
        connectDatabase: Yup.boolean().required(t("databaseRequired")),
        selectedContentLanguage: Yup.string().required(t("languageRequired")),
        textObject: Yup.string().required(t("topicRequired")),
        textTitle: Yup.string().required(t("titleRequired")),
        numberOfWords: Yup.number()
            .min(50, t("minimumWordsRequired"))
            .max(5000, t("maximumWordsExceeded"))
            .required(t("numberOfWordsRequired")),
        audience: Yup.string(),
        numberStories: Yup.number().min(1).max(20),
        requiredContent: Yup.array().of(Yup.string().max(80)),
        selectedFolders: Yup.array()
            .of(Yup.string())
            .when("connectDatabase", {
                is: true,
                then: (schema) => schema.min(1, t("atLeastOneFolderMustBeSelected")),
                otherwise: (schema) => schema.notRequired(),
            }),
    });

    const initialValues: CartelFormValues = {
        connectDatabase: false,
        selectedContentLanguage: "",
        textObject: "",
        textTitle: "",
        numberOfWords: 250,
        audience: "",
        numberStories: 1,
        requiredContent: [],
        selectedFolders: [],
        restrictToDatabase: false,
        brandVoiceId: "",
        audienceProfileId: "",
    };

    const handleSubmit = async (values: CartelFormValues) => {
        setLoadingTextGeneration(true);
        setShowStatusModal(true);
        setGenerationStatus("waiting");
        setErrorReason("generic");

        try {
            const response = await createCartelQueueAI({
                token: localStorage.getItem("token") || "",
                isFromDb: values.connectDatabase,
                language: values.selectedContentLanguage,
                textObject: values.textObject,
                numberOfWords: values.numberOfWords,
                audience: values.audience,
                textTitle: values.textTitle,
                numberStories: values.numberStories,
                requiredContent: values.requiredContent,
                queryFilters: values.selectedFolders.length > 0 ? { folderId: values.selectedFolders } : undefined,
                restrictToDatabase: values.restrictToDatabase,
                brandVoiceId: values.brandVoiceId && values.brandVoiceId !== "" ? values.brandVoiceId : null,
                audienceProfileId:
                    values.audienceProfileId && values.audienceProfileId !== "" ? values.audienceProfileId : null,
            });

            if (response?.id) {
                navigate(`/create/cartel/${response.id}`);
            } else {
                setGenerationStatus("error");
            }
        } catch (err) {
            if (isQuotaError(err)) {
                setErrorReason("quota");
                refetchTokens();
            }
            setGenerationStatus("error");
        } finally {
            setLoadingTextGeneration(false);
        }
    };

    const handlecartelAIAwaiting = useCallback(
        async (id: string) => {
            setLoadingTextGeneration(true);
            setShowStatusModal(true);
            setGenerationStatus("waiting");
            setWaitingRank(null);

            const fetchcartelAI = async () => {
                try {
                    const cartelAI = await getCartelAIWaiting({
                        token: localStorage.getItem("token") || "",
                        cartelAIId: id,
                    });

                    if (cartelAI?.failed) {
                        setLoadingTextGeneration(false);
                        setGenerationStatus("error");
                        return;
                    }

                    if (cartelAI?.waiting) {
                        if (typeof cartelAI.rank === "number") {
                            setWaitingRank(cartelAI.rank);
                        }
                        setTimeout(fetchcartelAI, 5000);
                    } else {
                        setLoadingTextGeneration(false);
                        if (cartelAI?.cartelAI) {
                            if (cartelAI.cartelAI.text === "Nothing found") {
                                setTextGenerated(t("nothingFoundText"));
                            } else {
                                setTextGenerated(cartelAI.cartelAI.text);
                            }
                            setGenerationStatus("success");
                            refetchTokens();
                        }
                        setWaitingRank(null);
                    }
                } catch (error) {
                    console.error("Error fetching audio AI data:", error);
                    setLoadingTextGeneration(false);
                    setGenerationStatus("error");
                }
            };

            fetchcartelAI();
        },
        [
            setLoadingTextGeneration,
            setShowStatusModal,
            setGenerationStatus,
            setWaitingRank,
            setTextGenerated,
            t,
            refetchTokens,
        ],
    );

    const handleNextStep = () => {
        navigate(`/create/cartel-remix/${id || ""}`);
    };

    const handleAddToProject = async (projectId: string, newProjectName: string) => {
        const token = localStorage.getItem("token");
        if (!token || !id) {
            return;
        }

        const cartelAI = await getCartelAIWaiting({ token: token, cartelAIId: id });

        if (cartelAI?.cartelAI) {
            if (projectId === "new") {
                await createProjectCartel({
                    token: token,
                    name: newProjectName,
                    cartelAIId: cartelAI.cartelAI.id,
                });
            } else {
                await addItemToProjectAI({
                    token: token,
                    projectId: projectId,
                    itemId: cartelAI.cartelAI.id,
                    itemType: AI_MODEL_TYPES.CARTEL,
                });
            }

            await refreshProjects();
        }
    };

    const remainingWords = tokenData?.remainigTextTokens;

    return (
        <div className={styles.page}>
            <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
                key={i18n.language}
                validateOnMount
            >
                {({ isSubmitting, submitForm, errors, values: { textTitle }, setFieldValue }) => {
                    // Store setFieldValue in ref for use in modal callback
                    setFieldValueRef.current = setFieldValue;

                    return (
                        <>
                            <div className={styles.work}>
                                <section className={styles.editor}>
                                    <div className={styles.editorHead}>
                                        <div>
                                            <h1>{t("textPrompt")}</h1>
                                            <p>{t("fillThisSectionToGenerateYourStoryYouCanEditItManuallyLater")}</p>
                                        </div>
                                        {remainingWords != null && (
                                            <span className={styles.token}>
                                                <Icon name="ic-sparkles" />
                                                {t("remainingWords")}&nbsp;: <b>{remainingWords.toLocaleString()}</b>
                                            </span>
                                        )}
                                    </div>
                                    <div className={styles.canvas}>
                                        {!textGenerated && !loadingTextGeneration ? (
                                            <div className={styles.canvasEmpty}>
                                                <div className={styles.empty}>
                                                    <div className={styles.emptyIco}>
                                                        <Icon name="ic-file-text" />
                                                    </div>
                                                    <h3>{t("yourTextWillAppearHere")}</h3>
                                                    <p>{t("yourCartelWillAppearHereWhenGenerationIsDone")}</p>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className={styles.canvasEditor}>
                                                <textarea
                                                    className="text-input-big"
                                                    placeholder={
                                                        loadingTextGeneration
                                                            ? t("pleaseWaitTextGenerationInProgress")
                                                            : t("yourCartelWillAppearHereWhenGenerationIsDone")
                                                    }
                                                    value={loadingTextGeneration ? "" : textGenerated}
                                                    rows={12}
                                                    disabled={false}
                                                    onChange={(e) => setTextGenerated(e.target.value)}
                                                />
                                            </div>
                                        )}
                                    </div>
                                </section>

                                <aside className={styles.params}>
                                    <div className={styles.paramsHead}>
                                        <Icon name="ic-settings" />
                                        {t("settings")}
                                    </div>
                                    <div className={styles.paramsBody}>
                                        <CartelCreationForm
                                            handlecartelAIAwaiting={handlecartelAIAwaiting}
                                            id={id}
                                            user={user}
                                            nonEmptyRagFolders={nonEmptyRagFolders}
                                            brandVoices={brandVoices}
                                            audienceProfiles={audienceProfiles}
                                            isCreateBrandVoiceModalOpen={isCreateBrandVoiceModalOpen}
                                            onOpenCreateBrandVoiceModal={() => setIsCreateBrandVoiceModalOpen(true)}
                                            onCloseCreateBrandVoiceModal={() => setIsCreateBrandVoiceModalOpen(false)}
                                            onBrandVoiceCreated={async (brandVoiceId: string) => {
                                                const voices = await getBrandVoices();
                                                setBrandVoices(voices);
                                            }}
                                            isCreateAudienceProfileModalOpen={isCreateAudienceProfileModalOpen}
                                            onOpenCreateAudienceProfileModal={() =>
                                                setIsCreateAudienceProfileModalOpen(true)
                                            }
                                            onCloseCreateAudienceProfileModal={() =>
                                                setIsCreateAudienceProfileModalOpen(false)
                                            }
                                            onAudienceProfileCreated={async (audienceProfileId: string) => {
                                                const profiles = await getAudienceProfiles();
                                                setAudienceProfiles(profiles);
                                            }}
                                        />
                                    </div>
                                    <div className={styles.paramsFoot}>
                                        <Tooltip
                                            title={
                                                isTextExhausted
                                                    ? t("quotaReachedTextTooltip")
                                                    : Object.keys(errors).length > 0
                                                      ? t("formIncompleteTooltip", {
                                                            fields: Object.values(errors).join(", "),
                                                        })
                                                      : ""
                                            }
                                            placement="top"
                                        >
                                            <div>
                                                <Button
                                                    label={
                                                        loadingTextGeneration
                                                            ? waitingRank !== null && waitingRank > 0
                                                                ? t("waitingInQueue", { rank: waitingRank })
                                                                : t("generating")
                                                            : t("generateCartel")
                                                    }
                                                    onClick={submitForm}
                                                    disabled={
                                                        isSubmitting ||
                                                        Object.keys(errors).length > 0 ||
                                                        isTextExhausted
                                                    }
                                                    variant="primary"
                                                    shape="default"
                                                    block
                                                    className={styles.generateBtn}
                                                    icon={
                                                        !loadingTextGeneration ? (
                                                            <Icon name="ic-sparkles" className="ic" />
                                                        ) : undefined
                                                    }
                                                />
                                            </div>
                                        </Tooltip>
                                        <div className={styles.secondaryActions}>
                                            {textGenerated && (
                                                <Button
                                                    label={t("nextStep")}
                                                    onClick={handleNextStep}
                                                    variant="ghost"
                                                />
                                            )}
                                            {textGenerated && textGenerated !== "" && textTitle && textTitle !== "" && (
                                                <AddToProjectButton onClick={() => setIsModalOpen(true)} />
                                            )}
                                        </div>
                                    </div>
                                </aside>
                            </div>

                            <AddCartelToProjectModal
                                isOpen={isModalOpen}
                                onClose={() => setIsModalOpen(false)}
                                onAddToProject={handleAddToProject}
                            />
                            <AIGenerationStatusModal
                                isOpen={showStatusModal}
                                onClose={() => setShowStatusModal(false)}
                                status={generationStatus}
                                rank={waitingRank}
                                errorTitle={errorReason === "quota" ? t("quotaReachedTitle") : undefined}
                                errorSubtitle={errorReason === "quota" ? t("quotaReachedTextSubtitle") : undefined}
                            />
                            <CreateBrandVoiceModal
                                isOpen={isCreateBrandVoiceModalOpen}
                                onClose={() => setIsCreateBrandVoiceModalOpen(false)}
                                onSuccess={async (brandVoiceId: string) => {
                                    // Refresh brand voices list
                                    const voices = await getBrandVoices();
                                    setBrandVoices(voices);
                                    // Set the newly created brand voice as selected
                                    if (setFieldValueRef.current) {
                                        setFieldValueRef.current("brandVoiceId", brandVoiceId || "");
                                    }
                                }}
                            />
                            <EditAudienceProfileModal
                                isOpen={isCreateAudienceProfileModalOpen}
                                onClose={() => setIsCreateAudienceProfileModalOpen(false)}
                                audienceProfile={null}
                                browserLanguage={i18n.language}
                                onProfileCreated={async (profile: AudienceProfile) => {
                                    // Refresh audience profiles list
                                    const profiles = await getAudienceProfiles();
                                    setAudienceProfiles(profiles);
                                    // Set the newly created audience profile as selected
                                    if (setFieldValueRef.current) {
                                        setFieldValueRef.current("audienceProfileId", profile.id || "");
                                    }
                                }}
                            />
                        </>
                    );
                }}
            </Formik>
        </div>
    );
};

export default CartelCreation;
